<template>
  <div class="inline-flex items-center">
    <template v-if="type === 'email'">
      <a-tooltip v-if="hasValid && variant === 'icon'" placement="top" title="Send Email">
        <div class="quick_action_item" @click="onEmailIconClick">
          <ThemeImage class="size-5" :icon="imageNames.EMAIL_GREY" alt="email_icon" />
        </div>
      </a-tooltip>

      <a-tooltip v-else-if="hasValid && variant === 'inline-button'" :title="local">
        <span class="truncate text-sm text_color">{{ local }}</span>
      </a-tooltip>

      <a-button
        v-else-if="variant === 'inline-button'"
        class="secondary_small min-w-24 flex items-center justify-center gap-1"
        :loading="isLoading"
        @click="accessValue"
      >
        <ThemeImage
          v-if="!isLoading"
          class="size-3 p-[1px] mb-[2px]"
          :icon="imageNames.WHITE_ENRICH_SEARCH"
          alt="enrich_search_icon"
        />
        <span class="font-semibold text-sm text_color">{{ accessEmailText }}</span>
      </a-button>

      <CustomDropdown
        v-else
        ref="dropdownRef"
        gap="10px"
        position="end"
        :relative-to-viewport="true"
        :disabled="isDisabled"
      >
        <template #trigger>
          <div class="quick_action_item">
            <a-tooltip placement="top" title="Send Email">
              <ThemeImage class="size-5" :icon="imageNames.EMAIL_GREY" alt="email_icon" />
            </a-tooltip>
          </div>
        </template>
        <template #default>
          <div class="flex flex-col gap-2 p-2">
            <span class="text-[13px] secondary_color">Email</span>
            <div v-if="notFound" class="flex flex-row items-center gap-1">
              <ThemeImage :icon="imageNames.ERROR" alt="error_icon" />
              <span class="text-sm text_color">Not available</span>
            </div>
            <div v-else class="flex flex-col gap-2">
              <span class="text-2xl font-semibold text_color">************</span>
              <a-button
                class="primary_medium min-w-48"
                :loading="isLoading"
                @click="accessValue"
              >
                <ThemeImage
                  v-if="!isLoading"
                  :icon="imageNames.ENRICH_SEARCH"
                  alt="enrich_search_icon"
                  class="size-4"
                />
                {{ accessEmailText }}
              </a-button>
            </div>
          </div>
        </template>
      </CustomDropdown>
    </template>

    <template v-else-if="type === 'phone'">
      <PlaceCall
        v-if="hasValid && variant === 'icon'"
        :source="entityType"
        :icon-action="true"
        :entity="callEntity"
        :multiple-entries="true"
        :trigger-open-dropdown="shouldOpenPlaceCall"
      />

      <a-tooltip v-else-if="hasValid && variant === 'inline-button'" :title="local">
        <span class="truncate text-sm text_color">{{ local }}</span>
      </a-tooltip>

      <a-button
        v-else-if="variant === 'inline-button'"
        class="secondary_small min-w-24 flex items-center justify-center gap-1"
        :loading="isLoading"
        @click="accessValue"
      >
        <ThemeImage
          v-if="!isLoading"
          class="size-3 p-[1px] mb-[2px]"
          :icon="imageNames.WHITE_ENRICH_SEARCH"
          alt="enrich_search_icon"
        />
        <span class="font-semibold text-sm">{{ accessPhoneText }}</span>
      </a-button>

      <CustomDropdown
        v-else
        ref="dropdownRef"
        gap="4px"
        position="end"
        :relative-to-viewport="true"
        :disabled="isDisabled"
      >
        <template #trigger>
          <div class="quick_action_item mt-1">
            <a-tooltip placement="top" title="Place a call">
              <ThemeImage class="size-5" :icon="imageNames.CALL_GREY" alt="call_icon" />
            </a-tooltip>
          </div>
        </template>
        <template #default>
          <div class="flex flex-col gap-2 p-2">
            <span class="text-[13px] secondary_color">Phone Number</span>
            <div v-if="notFound" class="flex flex-row items-center gap-1">
              <ThemeImage :icon="imageNames.ERROR" alt="error_icon" />
              <span class="text-sm text_color">Not available</span>
            </div>
            <div v-else class="flex flex-col gap-2">
              <span class="text-2xl font-semibold text_color">************</span>
              <a-button
                class="primary_medium min-w-48"
                :loading="isLoading"
                @click="accessValue"
              >
                <ThemeImage
                  v-if="!isLoading"
                  :icon="imageNames.ENRICH_SEARCH"
                  alt="enrich_search_icon"
                  class="size-4"
                />
                {{ accessPhoneText }}
              </a-button>
            </div>
          </div>
        </template>
      </CustomDropdown>
    </template>

    <template v-else-if="type === 'list'">
      <ProspectListDropdown
        :entity-type="entityType"
        :disabled="isDisabled"
        :lazy="true"
        @list-selected="onListSelected"
        @list-created="onListSelected"
      >
        <template #trigger>
          <div class="quick_action_item mt-1">
            <a-tooltip placement="top" title="Add to list">
              <ThemeImage class="size-5" :icon="imageNames.ORDERED_LIST_GREY" alt="add_to_list" />
            </a-tooltip>
          </div>
        </template>
      </ProspectListDropdown>
    </template>

    <template v-else-if="type === 'sequence'">
      <ProspectSequenceDropdown
        :disabled="isDisabled"
        :lazy="true"
        @sequence-selected="onSequenceSelected"
      >
        <template #trigger>
          <div class="quick_action_item mt-1">
            <a-tooltip placement="top" title="Add to sequence">
              <ThemeImage class="size-5" :icon="imageNames.ADD_TO_SEQUENCE_GREY" alt="add_to_sequence" />
            </a-tooltip>
          </div>
        </template>
      </ProspectSequenceDropdown>

      <SetupSenderDetailsModal
        v-if="sequenceAddition.state.modalStates.setupSenderDetailsModal && pendingSeq"
        v-model:visible="sequenceAddition.state.modalStates.setupSenderDetailsModal"
        :sequence-id="pendingSeq.id"
        :sequence-name="pendingSeq.name"
        :pending-validation-data="sequenceAddition.state.pendingValidationData || null"
        source="prospect_table_actions"
        @save="onSenderSave"
        @close="onSenderCancel"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, inject, ref, watch } from "vue";
import { message } from "ant-design-vue";
import { useRouter } from "vue-router";
import eventBus from "@/src/utils/eventBus.js";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import PlaceCall from "@/src/components/telephony_components/modules/PlaceCall.vue";
import ProspectListDropdown from "@/src/components/find_components/ProspectListDropdown.vue";
import ProspectSequenceDropdown from "@/src/components/find_components/ProspectSequenceDropdown.vue";
import SetupSenderDetailsModal from "@/src/components/sequences_components/SetupSenderDetailsModal.vue";
import { useSequenceAddition } from "@/src/composables/useSequenceAddition.js";
import { showSuccessMessage } from "@/src/utils/utils.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const router = useRouter();

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (v) => ["email", "phone", "list", "sequence"].includes(v),
  },
  entityId: { type: [String, Number], required: true },
  entityType: { type: String, required: true },
  value: { type: String, default: "" },
  variant: { type: String, default: "icon" },
});

const emit = defineEmits(["enriched", "added"]);

const handlers = inject("prospectTableActionsHandlers", {});
const slot = computed(() => handlers?.[props.type] || null);
const isDisabled = computed(() => !slot.value);

const dropdownRef = ref(null);
const isLoading = ref(false);
const notFound = ref(false);

const local = ref(props.value);
const extras = ref({});
watch(() => props.value, (v) => { local.value = v; });
watch(() => props.entityId, () => { extras.value = {}; });

const hasValid = computed(
  () => !!local.value && local.value !== "email_not_unlocked@domain.com",
);

const accessEmailText = computed(() => (isLoading.value ? "Accessing..." : "Access Email"));
const accessPhoneText = computed(() => (isLoading.value ? "Accessing..." : "Access Phone Number"));

const shouldOpenPlaceCall = ref(false);
const callEntity = computed(() => ({
  id: props.entityId,
  phone_number: local.value,
  phone_numbers: extras.value.phone_numbers || [],
}));

const openComposer = () => {
  eventBus.emit("open-email-composer", {
    entity: { id: props.entityId, email: local.value, ...extras.value },
    source: props.entityType,
  });
};

const triggerPlaceCall = () => {
  shouldOpenPlaceCall.value = !shouldOpenPlaceCall.value;
};

const onEmailIconClick = () => {
  if (hasValid.value) openComposer();
};

const accessValue = async () => {
  if (!slot.value?.enrich) return;
  isLoading.value = true;
  notFound.value = false;
  try {
    const r = (await slot.value.enrich({
      entityId: props.entityId,
      entityType: props.entityType,
    })) || {};

    if (r.value) {
      local.value = r.value;
      extras.value = { ...r };
      dropdownRef.value?.closeDropdown();
      if (props.variant === "icon") {
        if (props.type === "email") openComposer();
        else if (props.type === "phone") triggerPlaceCall();
      }
    } else {
      notFound.value = true;
      message.info(props.type === "email" ? "No email found" : "No phone number found");
    }

    if (r.value || r.lead_id || r.company_id) {
      emit("enriched", { entityId: props.entityId, ...r });
    }
  } catch (error) {
    const fallback = props.type === "email" ? "Failed to access email" : "Failed to access phone number";
    message.error(error?.response?.data?.message || error?.message || fallback);
  } finally {
    isLoading.value = false;
  }
};

const sequenceAddition = useSequenceAddition();
const pendingSeq = ref(null);

const onListSelected = async (list) => {
  const targetId = list?.id;
  const targetName = list?.name || list?.list_name;
  if (!targetId) return;
  try {
    const ok = await slot.value?.add?.({
      entityId: props.entityId,
      entityType: props.entityType,
      targetId,
    });
    if (ok) {
      showSuccessMessage(router, 1, props.entityType, { id: targetId, name: targetName });
      emit("added", { entityId: props.entityId, targetId, targetName });
    }
  } catch (error) {
    message.error(error?.response?.data?.message || error?.message || "Failed to add to list");
  }
};

const onSequenceSelected = ({ id, name }) => {
  pendingSeq.value = { id, name };
  sequenceAddition.resetState();
  sequenceAddition.setDirectValidationData(1, "DIRECT");
  sequenceAddition.setModalState("setupSenderDetailsModal", true);
};

const onSenderSave = async (setup) => {
  sequenceAddition.setModalState("setupSenderDetailsModal", false);
  const seq = pendingSeq.value;
  if (!seq) return;
  try {
    const ok = await slot.value?.add?.({
      entityId: props.entityId,
      entityType: props.entityType,
      targetId: seq.id,
      targetConfigId: setup?.configId,
    });
    if (ok) {
      showSuccessMessage(router, 1, props.entityType, null, { id: seq.id, name: seq.name });
      emit("added", { entityId: props.entityId, targetId: seq.id, targetName: seq.name });
    }
  } catch (error) {
    message.error(error?.response?.data?.message || error?.message || "Failed to add to sequence");
  } finally {
    pendingSeq.value = null;
  }
};

const onSenderCancel = () => {
  pendingSeq.value = null;
};
</script>
